import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AlertService, AlertType } from '../alert.service';
import {
  CreateSectionRequest,
  IPagination,
  SearchParamsType,
  Section,
  SectionSearchRequest,
} from '../../models';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class SectionsService {
  // injects
  private readonly alertService = inject(AlertService);
  private readonly httpClient = inject(HttpClient);
  private readonly translate = inject(TranslateService);

  // api urls
  private urlBase: string = 'api/performance-management/section';

  // signals
  sections = signal<Section[]>([]);
  filteredSections = signal<IPagination<Section>>({
    content: [],
    totalElements: 0,
    config: {
      sortColumns: [],
    },
  });
  sectionsById = computed(() => {
    return this.sections().reduce(
      (acc, section) => {
        acc[section.id] = section;
        return acc;
      },
      {} as Record<number, Section>,
    );
  });

  constructor() {
    this.getAllSections();
  }

  searchSections(request: SectionSearchRequest): void {
    const props: SearchParamsType = {};

    if (request.search) {
      props['name'] = request.search;
    }

    if (typeof request.paginate !== 'undefined') {
      props['paginate'] = request.paginate;
    }

    const params = request.params ? request.params.appendAll(props) : props;
    this.httpClient
      .get<IPagination<Section>>(`${this.urlBase}/overview`, { params })
      .pipe(catchError(this.handleError), tap(this.filteredSections.set))
      .subscribe();
  }

  getAllSections() {
    this.httpClient
      .get<Section[]>(`${this.urlBase}/list/sections`)
      .pipe(catchError((error) => this.handleError(error)))
      .subscribe((response) => this.sections.set(response));
  }

  createSection({ name, description, orderNumber }: CreateSectionRequest): Observable<Section> {
    return this.httpClient
      .post<Section>(`${this.urlBase}/create`, { name, description, orderNumber })
      .pipe(
        tap(() => this.handleSuccess(this.translate.instant('SHARED.SUCCESS'))),
        tap(() => this.getAllSections()),
        catchError((error) => this.handleError(error)),
      );
  }

  deleteById(id: number): Observable<Section> {
    return this.httpClient.delete<Section>(`${this.urlBase}/${id}`).pipe(
      tap(() => this.handleSuccess(this.translate.instant('SHARED.SUCCESS'))),
      tap(() => this.getAllSections()),
      catchError((error) => this.handleError(error)),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.alertService.showAlert(error.error, AlertType.error);
    return throwError(() => error);
  }

  private handleSuccess(message: string) {
    this.alertService.showAlert(message, AlertType.success);
  }
}
